import LoadingSpinner from 'app/components/Common/LoadingSpinner';
import { OveitIcon } from 'app/components/OveitIcon';
import axios, { AxiosError } from 'axios';
import React from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  API_REQUEST_STATUS_FAIL,
  API_REQUEST_STATUS_PENDING,
  API_REQUEST_STATUS_SUCCESS,
} from 'utils/static/constants';
import { Country } from 'utils/static/countries';
import allStates, { CountryState } from 'utils/static/states';

interface TaxIdFieldProps {
  hasEnabledPO: boolean;
  selectedCountry: Country | null;
  selectedBilling: 'individual' | 'company';
  requestStatus: string;
  setRequestStatus: (status: string) => void;
  setSelectedState: (state: CountryState) => void;
}

const TaxIdField: React.FC<TaxIdFieldProps> = ({
  hasEnabledPO,
  selectedCountry,
  setSelectedState,
  requestStatus,
  selectedBilling,
  setRequestStatus,
}) => {
  const { t } = useTranslation();
  const {
    register,
    getValues,
    setValue,
    formState: { errors },
    setError,
    clearErrors,
  } = useFormContext();

  const handleVerifyAndAutoFillCompanyData = async () => {
    const taxID = getValues('tax_id');

    if (taxID.length < 2 || taxID.length > 10) {
      setError('tax_id', {
        type: 'required',
        message: 'invalidTaxCode',
      });
      return;
    }

    setRequestStatus(API_REQUEST_STATUS_PENDING);
    try {
      const {
        data: { state, city, address, companyName },
      } = await axios.post(
        `${process.env.REACT_APP_API_URL}/romanian-company/${taxID}`,
      );
      clearErrors('tax_id');
      clearErrors('name');
      clearErrors('city');
      clearErrors('address');
      clearErrors('state');

      setValue('name', companyName);
      setValue('city', city);
      setValue('address', address.replaceAll('  ', ''));

      const findState = allStates.find(
        ({ name }) => name.toLowerCase() === state.toLowerCase(),
      );

      if (findState) {
        setSelectedState(findState);
      }

      setRequestStatus(API_REQUEST_STATUS_SUCCESS);
    } catch (error) {
      setRequestStatus(API_REQUEST_STATUS_FAIL);

      if (error instanceof AxiosError) {
        if (error.response?.status === 500) {
          setError('tax_id', {
            type: 'required',
            message: 'anErrorOccured',
          });
        } else {
          setError('tax_id', {
            type: 'required',
            message: 'invalidTaxCode',
          });
        }
      }
    }
  };

  return (
    <div className={`w-full ${hasEnabledPO ? 'sm:w-1/2' : ''}`}>
      <label
        className={`label-input !flex gap-[8px] ${
          selectedCountry?.id !== 184 ? 'mandatory' : ''
        }`}
        htmlFor="tax_id"
      >
        {/*VAT number - ue*/}
        {/*Tax ID - non ue*/}
        <p className="p3-semibold">
          {selectedCountry && selectedCountry.eu
            ? t('registrationPage.vatNumber')
            : t('registrationPage.taxID')}
        </p>
        {requestStatus === API_REQUEST_STATUS_PENDING ? (
          <LoadingSpinner variant="small" />
        ) : null}
        {requestStatus === API_REQUEST_STATUS_SUCCESS ? (
          <OveitIcon name="solidGreenCheck" />
        ) : null}
      </label>
      <div className="relative">
        <input
          className={`standard-input w-full ${
            errors.tax_id?.message && 'error'
          }`}
          type="text"
          {...register('tax_id', {
            required: {
              value:
                selectedBilling === 'company' && selectedCountry?.id !== 184,
              message: 'requiredField',
            },
          })}
          placeholder={
            selectedCountry && selectedCountry.tax_code_example
              ? selectedCountry.tax_code_example
              : ''
          }
        />
        {selectedCountry?.id === 141 ? (
          <>
            <button
              type="button"
              className={`button btn-blue flex items-center absolute top-0 bottom-0 right-[4px] mt-auto mb-auto h-[32px] ${
                requestStatus === API_REQUEST_STATUS_PENDING ? 'disabled' : ''
              }`}
              onClick={handleVerifyAndAutoFillCompanyData}
              disabled={requestStatus === API_REQUEST_STATUS_PENDING}
            >
              {t('registrationPage.verifyAndAutoFill')}
            </button>
          </>
        ) : null}
      </div>
      {errors.tax_id?.message && (
        <p className="standard-input-error">
          {t(`inputErrors.${errors.tax_id.message}`)}
        </p>
      )}
    </div>
  );
};

export default TaxIdField;
